/**
 * Admin API client
 */

import apiClient from '@/core/api/client'
import {
  UserWithBalances,
  AdminUserListItem,
  Role,
  LeaveType,
  Division,
  UpdateLeaveBalanceRequest,
  AssignRoleRequest,
  CreateDivisionRequest,
  UpdateDivisionRequest,
  UpdateUserDivisionRequest,
  MicrosoftSyncResponse,
  AuditLogEntry,
  UpdateProfileRequest,
  UpdateManagerRequest,
} from '../types/admin.types'
import { PaginatedResponse } from '@/shared/types/common.types'

const adminApi = {
  /**
   * Get a single user by ID with leave balances
   */
  getUser: async (userId: string): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.get(`/v1/admin/users/${userId}`)
    return response.data
  },

  /**
   * Get all users with pagination and search
   */
  getUsers: async (params?: {
    search?: string
    include_inactive?: boolean
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<AdminUserListItem>> => {
    const response = await apiClient.get('/v1/admin/users', { params })
    return {
      data: response.data.users,
      pagination: response.data.pagination,
    }
  },

  /**
   * Assign a role to a user
   */
  assignRole: async (
    userId: string,
    data: AssignRoleRequest
  ): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.post(`/v1/admin/users/${userId}/roles`, data)
    return response.data
  },

  /**
   * Remove a role from a user
   */
  removeRole: async (userId: string, roleName: string): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.delete(`/v1/admin/users/${userId}/roles/${roleName}`)
    return response.data
  },

  /**
   * Update user leave balance
   */
  updateLeaveBalance: async (
    userId: string,
    data: UpdateLeaveBalanceRequest
  ): Promise<any> => {
    const response = await apiClient.put(`/v1/admin/users/${userId}/leave-balance`, data)
    return response.data
  },

  /**
   * Delete user leave balance
   */
  deleteLeaveBalance: async (
    userId: string,
    leaveTypeId: string,
    year?: number
  ): Promise<{ message: string }> => {
    const params = year ? { year } : {}
    const response = await apiClient.delete(
      `/v1/admin/users/${userId}/leave-balance/${leaveTypeId}`,
      { params }
    )
    return response.data
  },

  /**
   * Update user division
   */
  updateUserDivision: async (
    userId: string,
    data: UpdateUserDivisionRequest
  ): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.put(`/v1/admin/users/${userId}/division`, data)
    return response.data
  },

  /**
   * Update user status (activate/deactivate)
   */
  updateUserStatus: async (
    userId: string,
    isActive: boolean
  ): Promise<{ message: string; user: UserWithBalances }> => {
    const response = await apiClient.put(`/v1/admin/users/${userId}/status`, { is_active: isActive })
    return response.data
  },

  /**
   * Delete a user (soft delete)
   */
  deleteUser: async (userId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/v1/admin/users/${userId}`)
    return response.data
  },

  /**
   * Get all roles
   */
  getRoles: async (): Promise<{ roles: Role[] }> => {
    const response = await apiClient.get('/v1/admin/roles')
    return response.data
  },

  /**
   * Get all leave types
   */
  getLeaveTypes: async (): Promise<{ leave_types: LeaveType[] }> => {
    const response = await apiClient.get('/v1/admin/leave-types')
    return response.data
  },

  // ==================== Division APIs ====================

  /**
   * Get all divisions
   */
  getDivisions: async (params?: {
    include_inactive?: boolean
  }): Promise<{ divisions: Division[] }> => {
    const response = await apiClient.get('/v1/admin/divisions', { params })
    return response.data
  },

  /**
   * Create a new division
   */
  createDivision: async (data: CreateDivisionRequest): Promise<{ division: Division }> => {
    const response = await apiClient.post('/v1/admin/divisions', data)
    return response.data
  },

  /**
   * Update a division
   */
  updateDivision: async (
    divisionId: string,
    data: UpdateDivisionRequest
  ): Promise<{ division: Division }> => {
    const response = await apiClient.put(`/v1/admin/divisions/${divisionId}`, data)
    return response.data
  },

  /**
   * Delete a division
   */
  deleteDivision: async (divisionId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/v1/admin/divisions/${divisionId}`)
    return response.data
  },

  // ==================== Microsoft 365 Sync ====================

  /**
   * Sync users from Microsoft 365 / Azure AD
   */
  syncMicrosoftUsers: async (params?: {
    sync_managers?: boolean
  }): Promise<MicrosoftSyncResponse> => {
    const response = await apiClient.post('/v1/admin/users/sync-microsoft', null, { params })
    return response.data
  },

  /**
   * Get paginated audit log for a user (as the subject/resource)
   */
  getUserAuditLog: async (
    userId: string,
    params?: { page?: number; limit?: number }
  ): Promise<{ audit_log: AuditLogEntry[]; pagination: { page: number; per_page: number; total: number; pages: number } }> => {
    const response = await apiClient.get(`/v1/admin/users/${userId}/audit-log`, { params })
    return response.data
  },

  /**
   * Manually override profile fields and lock them from Azure AD sync
   */
  updateUserProfile: async (
    userId: string,
    data: UpdateProfileRequest
  ): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.put(`/v1/admin/users/${userId}/profile`, data)
    return response.data
  },

  /**
   * Manually assign (or clear) a user's manager
   */
  updateUserManager: async (
    userId: string,
    data: UpdateManagerRequest
  ): Promise<{ user: UserWithBalances }> => {
    const response = await apiClient.put(`/v1/admin/users/${userId}/manager`, data)
    return response.data
  },

  /**
   * Remove the manual-management lock so Azure AD sync can overwrite fields again
   */
  resetProfileToAzure: async (
    userId: string
  ): Promise<{ message: string; user: UserWithBalances }> => {
    const response = await apiClient.post(`/v1/admin/users/${userId}/reset-profile`)
    return response.data
  },
}

export default adminApi
